/**
 * Guided UI for configuring rollup/aggregation parameters
 * Provides parameter picker with descriptions and validation
 */

import React, { useState } from 'react';
import { Card, Select, Input, Switch, Button, Space, Tooltip, Typography } from 'antd';
import { DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { 
  ROLLUP_PARAMETER_CATALOG, 
  RollupParameterOption,
} from '../../config/rollupParameterCatalog';
import { RollupParameters, ParameterDefinition, createParameterDefinition } from '../../types/transitionParameters';
import './RollupConfigurationPanel.css';

const { Text, Paragraph } = Typography;
const { Option } = Select;

interface RollupConfigurationPanelProps {
  rollupParams: RollupParameters;
  onChange: (params: RollupParameters) => void;
  artifactType?: string;  // For filtering relevant parameters
  disabled?: boolean;
}

export const RollupConfigurationPanel: React.FC<RollupConfigurationPanelProps> = ({
  rollupParams,
  onChange,
  artifactType,
  disabled = false,
}) => {
  const [selectedParameter, setSelectedParameter] = useState<string | undefined>();
  const [useCase, setUseCase] = useState<string>('revenue');

  // Get aggregation type to filter parameters
  const aggregationType = rollupParams.aggregation_type?.value as 'time_based' | 'parent_based' | undefined;

  // Get available parameters (not yet added)
  const availableParams = Object.values(ROLLUP_PARAMETER_CATALOG).filter(param => {
    // Already added?
    if (rollupParams[param.key as keyof RollupParameters]) return false;
    
    // Check dependencies
    if (param.dependsOn === 'aggregation_enabled') {
      if (!rollupParams.aggregation_enabled?.value) return false;
    }
    
    if (param.dependsOn === 'aggregation_type') {
      if (!aggregationType) return false;
    }
    
    // Filter based on aggregation type
    if (aggregationType === 'time_based') {
      // Hide parent-based parameters for time-based aggregation
      if (['parent_artifact_ref_field', 'parent_artifact_type'].includes(param.key)) {
        return false;
      }
    } else if (aggregationType === 'parent_based') {
      // Hide time-based parameters for parent-based aggregation
      if (['aggregation_period', 'bucket_key_format', 'bucket_key_field', 'window_start_field', 'window_end_field'].includes(param.key)) {
        return false;
      }
    }
    
    return true;
  });

  // Get current parameters
  const currentParams = Object.keys(rollupParams).map(key => 
    ROLLUP_PARAMETER_CATALOG[key]
  ).filter(Boolean);

  // Add a parameter
  const handleAddParameter = (paramKey: string) => {
    const paramDef = ROLLUP_PARAMETER_CATALOG[paramKey];
    if (!paramDef) return;

    const newParam = createParameterDefinition(
      paramDef.type as any,
      undefined,
      { description: paramDef.description }
    );

    onChange({
      ...rollupParams,
      [paramKey]: newParam,
    });
    
    setSelectedParameter(undefined);
  };

  // Remove a parameter
  const handleRemoveParameter = (paramKey: string) => {
    const updated = { ...rollupParams };
    delete updated[paramKey as keyof RollupParameters];
    onChange(updated);
  };

  // Update parameter value
  const handleUpdateValue = (paramKey: string, value: any) => {
    const param = rollupParams[paramKey as keyof RollupParameters];
    if (!param) return;

    onChange({
      ...rollupParams,
      [paramKey]: {
        ...param,
        value,
      },
    });
  };

  // Render parameter input based on type
  const renderParameterInput = (paramDef: RollupParameterOption, currentValue: ParameterDefinition) => {
    if (paramDef.type === 'boolean') {
      return (
        <Switch
          checked={currentValue.value ?? false}
          onChange={(checked) => handleUpdateValue(paramDef.key, checked)}
          disabled={disabled}
        />
      );
    }

    if (paramDef.options) {
      return (
        <Select
          style={{ width: '100%' }}
          value={currentValue.value ?? undefined}
          onChange={(value) => handleUpdateValue(paramDef.key, value)}
          placeholder={paramDef.placeholder}
          disabled={disabled}
        >
          {paramDef.options.map(opt => (
            <Option key={opt.value} value={opt.value}>
              <div>
                <div>{opt.label}</div>
                {opt.description && (
                  <div style={{ fontSize: '11px', color: '#888' }}>{opt.description}</div>
                )}
              </div>
            </Option>
          ))}
        </Select>
      );
    }

    return (
      <Input
        value={currentValue.value ?? ''}
        onChange={(e) => handleUpdateValue(paramDef.key, e.target.value)}
        placeholder={paramDef.placeholder}
        disabled={disabled}
      />
    );
  };

  return (
    <div className="rollup-configuration-panel">
      {/* Use Case Selector */}
      <Card size="small" style={{ marginBottom: 16, backgroundColor: '#f9fafb' }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong>Configuration Template</Text>
          <Select
            style={{ width: '100%' }}
            value={useCase}
            onChange={setUseCase}
            placeholder="Select use case for parameter suggestions"
          >
            <Option value="revenue">Revenue (Recognized/Bookings)</Option>
            <Option value="expenses">Expenses</Option>
            <Option value="supply">Supply/Inventory</Option>
            <Option value="demand">Demand</Option>
            <Option value="delivery">Delivery</Option>
          </Select>
          <Paragraph style={{ margin: 0, fontSize: '12px', color: '#666' }}>
            <InfoCircleOutlined /> Select a use case to filter relevant parameters
          </Paragraph>
        </Space>
      </Card>

      {/* Current Parameters */}
      <Card 
        title="Configured Parameters" 
        size="small" 
        style={{ marginBottom: 16 }}
        extra={
          <Tooltip title="Add parameter from catalog">
            <Select
              style={{ width: 250 }}
              placeholder="Add parameter..."
              value={selectedParameter}
              onChange={handleAddParameter}
              disabled={disabled}
              showSearch
              filterOption={(input, option) => {
                // Search by the parameter key which is used in the Option value
                const paramKey = option?.value as string;
                const param = ROLLUP_PARAMETER_CATALOG[paramKey];
                if (!param) return false;
                
                const searchText = `${param.label} ${param.description}`.toLowerCase();
                return searchText.includes(input.toLowerCase());
              }}
            >
              {availableParams
                .filter(p => p.useCases.includes(useCase))
                .map(param => (
                  <Option key={param.key} value={param.key}>
                    <Space>
                      <Text>{param.label}</Text>
                      {param.required && <Text type="danger">*</Text>}
                    </Space>
                  </Option>
                ))
              }
            </Select>
          </Tooltip>
        }
      >
        {currentParams.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px', color: '#999' }}>
            No rollup parameters configured. Add parameters from the dropdown above.
          </div>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {currentParams.map((paramDef) => {
              if (!paramDef) return null;
              const currentValue = rollupParams[paramDef.key as keyof RollupParameters];
              if (!currentValue) return null;

              return (
                <Card key={paramDef.key} size="small" style={{ backgroundColor: '#fafafa' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space>
                        <Text strong>{paramDef.label}</Text>
                        {paramDef.required && <Text type="danger">*</Text>}
                        <Tooltip title={paramDef.tooltip}>
                          <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                      </Space>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveParameter(paramDef.key)}
                        disabled={disabled || paramDef.required}
                      />
                    </Space>
                    <Paragraph style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                      {paramDef.description}
                    </Paragraph>
                    {renderParameterInput(paramDef, currentValue)}
                  </Space>
                </Card>
              );
            })}
          </Space>
        )}
      </Card>

      {/* Validation Summary */}
      {currentParams.length > 0 && (
        <Card size="small" title="Configuration Summary" style={{ backgroundColor: '#f0f9ff' }}>
          <Space direction="vertical" size="small">
            <Text>Total parameters: {currentParams.length}</Text>
            <Text>Required parameters: {currentParams.filter(p => p?.required).length}</Text>
            {currentParams.filter(p => p?.required && !rollupParams[p.key as keyof RollupParameters]?.value).length > 0 && (
              <Text type="warning">
                Missing required values: {currentParams.filter(p => p?.required && !rollupParams[p.key as keyof RollupParameters]?.value).length}
              </Text>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

